<template>
    <div class="capper">
        <div class="capper-padded">
            <div v-if="label || section === 'home'" class="block block-grid-label" data-aos="custom-fade-in">
                <div :class="['rte', (section === 'home') ? 'no-max-width' : '' ]">
                    <img v-if="section === 'home' && outerIndex === 1" src="/assets/site/images/txt-ideas-executed.png"/>
                    <div v-else-if="section === 'home' && outerIndex === 0" style="display: none"></div>
                    <div v-else class="h3" v-html="getGridLabel(label)"></div>
                </div>
            </div>

            <div v-if="section === 'home' && outerIndex === 1" class="home-filters">
                <template v-for="(item, index) in filtersLinkouts" :key="'hf'+index">
                    <div
                        class="filter link-underline-leftright-dark"
                        :data-slug="item.slug"
                        @click="clickFilter(item)"
                    >
                        {{ item.title }}
                    </div>
                    <div v-if="index !== filtersLinkouts.length - 1" class="dot"></div>
                </template>
            </div>

            <div v-if="type === 'work' && processedJson.length === 0">
                <div class="rte right">
                    <p>No results.  Please expand the filter.</p>
                </div>
            </div>

            <div class="block block-grid" ref="container">
                <!-- Loop through paginatedJson which applies filters + pagination -->
                <div
                    v-for="(item, index) in paginatedJson"
                    :key="`griditem${Math.random()}${index}`"
                    :class="[
            'grid-item',
            item.itemType.value,
            getCategoryTags(item),
            (item.isCarousel) ? 'carousel' : 'image'
          ]"
                    :style="getItemStyle(item, index)"
                >
                    <router-link v-if="item.linkout?.url" :to="item.linkout.url">
                        <GridItemCarousel :json="item.graphic" :rollover="true" />
                    </router-link>
                    <template v-else>
                        <GridItemCarousel :json="item.graphic" :rollover="false" />
                    </template>
                </div>
            </div>

            <div v-if="paginate && visibleCount < processedJson.length"
                 class="btn-holder-outer"
                 ref="loadMoreTrigger"
            >
                <div class="btn-holder">
                    <button class="btn" @click="loadMore"><span>Load More</span></button>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
import { isMobile } from 'mobile-device-detect'
import { mapState } from "vuex";
import debounce from 'lodash/debounce'
import gh from '@/helpers/gh'
import GridItemCarousel from "@/components/blocks/GridItemCarousel"

export default {
    name: "GridBlock",
    components: {
        GridItemCarousel,
    },
    props: {
        json: {
            type: Array,
            required: true,
            default: null,
        },
        filtersLinkouts: {
            type: Array,
            required: false,
            default: null,
        },
        label: {
            type: String,
            required: false,
            default: null,
        },
        type: {
            type: String,
            required: false,
            default: 'base',
        },
        section: {
            type: String,
            required: true,
        },
        outerIndex: {
            type: Number,
            required: false,
            default: 0,
        },
        // Number of items to show per “page.” If null, no pagination.
        paginate: {
            type: Number,
            required: false,
            default: null,
        },
    },
    data() {
        return {
            gh: gh,
            window: window,
            containerWidth: 0,
            processedJson: [],
            heights: {},
            filterToggler: Math.random(),
            mutableJson: [],
            // How many items are currently visible
            visibleCount: this.paginate || 0,
            loadMoreObserver: null, //for scroll more load
        };
    },
    watch: {
        selectedFilters: {
            handler() {
                this.filterToggler = Math.random();
                this.masterGridFormatter();
            },
            deep: true,
        },
        processedJson: {
            handler(newVal) {
                // Reset visibleCount whenever the filtered/processed list changes
                if (this.paginate) {
                    this.visibleCount = this.paginate;
                } else {
                    this.visibleCount = newVal.length;
                }
            },
            immediate: true,
        },
    },
    mounted() {
        this.initFormatter();
        this.debouncedResizer = debounce(this.resizer, 150);
        this.window.addEventListener('resize', this.debouncedResizer);
        this.window.addEventListener('orientationchange', this.siteOrientationChange);

        this.masterGridFormatter();
        setTimeout(this.debouncedResizer, 100);

        this.loadMoreButtonScrollInit()
    },
    beforeUnmount() {
        this.window.removeEventListener('resize', this.debouncedResizer);

        if (this.loadMoreObserver) {
            this.loadMoreObserver.disconnect();
            this.loadMoreObserver = null;
        }
    },
    computed: {
        ...mapState({
            selectedFilters: state => state.selectedFilters,
        }),
        filteredJson() {
            if (!this.filterToggler || this.selectedFilters.length === 0) {
                return this.mutableJson;
            }
            return this.mutableJson.filter(item => {
                const cats = (item.projectCategory && Array.isArray(item.projectCategory))
                    ? item.projectCategory
                    : (item.linkout?.cats || []);
                if (!cats.length) return false;
                const catSlugs = cats.map(c => c.slug);
                return this.selectedFilters.every(sel => catSlugs.includes(sel.slug));
            });
        },
        paginatedJson() {
            if (this.paginate) {
                return this.processedJson.slice(0, this.visibleCount);
            }
            return this.processedJson;
        },
        nextBatchSize() {
            const remaining = this.processedJson.length - this.visibleCount;
            return remaining >= this.paginate ? this.paginate : remaining;
        },
    },
    methods: {
        loadMoreButtonScrollInit() {
            this.$nextTick(() => {
                const options = { root: null, rootMargin: '0px', threshold: 0.1 };
                this.loadMoreObserver = new IntersectionObserver(([entry]) => {
                    if (entry.isIntersecting && this.visibleCount < this.processedJson.length) {
                        this.loadMore();
                    }
                }, options);

                const triggerEl = this.$refs.loadMoreTrigger;
                if (triggerEl) this.loadMoreObserver.observe(triggerEl);
            });
        },
        // runs once on json
        initFormatter() {
            this.mutableJson = [...this.json];
            for (let i = 0; i < this.mutableJson.length; i++) {
                let currentItem = { ...this.mutableJson[i] };
                if (!currentItem.itemType) {
                    currentItem.itemType = {
                        label: "Single Column",
                        selected: true,
                        valid: true,
                        value: "singleColumn"
                    };
                    currentItem.isCarousel = true;
                    this.mutableJson[i] = currentItem;
                }
            }
        },
        siteOrientationChange() {
            this.masterGridFormatter();
        },
        resizer() {
            if (!isMobile) {
                this.masterGridFormatter();
            }
        },
        masterGridFormatter() {
            this.containerWidth = this.$refs.container?.offsetWidth;
            this.reorderJsonForOptimalPairings();
            this.setHeights();
        },
        getItemStyle(item, index) {
            let height = this.heights[index] || 'auto';
            let width = Math.floor(this.getItemWidth(item, height));
            let ret = {
                height: `${height}px`,
                width: `${width}px`,
            };
            if (item.itemType.value === 'singleColumn') {
                let aspectRatio = 16/9;
                if (item && item.graphic?.[0].videoWidth && item.graphic?.[0].videoHeight) {
                    aspectRatio = item.graphic[0].videoWidth / item.graphic?.[0].videoHeight;
                } else if (item && item.graphic?.[0].image?.[0]) {
                    aspectRatio = item.graphic?.[0].image[0].aspectRatio;
                }
                ret.aspectRatio = aspectRatio;
            }
            return ret;
        },
        getItemWidth(item, height) {
            if (item && item.graphic?.[0].videoWidth && item.graphic?.[0].videoHeight) {
                const aspectRatio = item.graphic[0].videoWidth / item.graphic?.[0].videoHeight;
                return height * aspectRatio;
            } else if (item && item.graphic?.[0].image && item.graphic?.[0].image?.[0]) {
                const aspectRatio = item.graphic?.[0].image?.[0].aspectRatio;
                return height * aspectRatio;
            }
            return 'auto';
        },
        setHeights() {
            const heights = {};
            let previousDouble = null;

            this.processedJson.forEach((item, index) => {
                let isImage = !!(item.graphic?.[0].image?.[0]);
                let isManualAspectRatio = !!(item && item.graphic?.[0].videoWidth && item.graphic?.[0].videoHeight);
                let doIt = isImage || isManualAspectRatio;

                if (item.itemType.value === 'doubleColumn' && doIt) {
                    let isImagePrev = !!(previousDouble && previousDouble.item.graphic?.[0].image?.[0]);
                    let isManualAspectPrev = !!(
                        previousDouble &&
                        previousDouble.item &&
                        previousDouble.item.graphic?.[0].videoWidth &&
                        previousDouble.item.graphic?.[0].videoHeight
                    );
                    let doItPrev = isImagePrev || isManualAspectPrev;

                    if (doItPrev) {
                        const combinedHeight = this.calculateCombinedHeight(previousDouble.item, item);
                        heights[previousDouble.index] = combinedHeight;
                        heights[index] = combinedHeight;
                        previousDouble = null;
                    } else {
                        previousDouble = { item, index };
                    }
                } else {
                    heights[index] = 'auto';
                }
            });

            if (previousDouble !== null) {
                let item = previousDouble.item;
                let aspectRatio;
                if (item && item.graphic?.[0].videoWidth && item.graphic?.[0].videoHeight) {
                    aspectRatio = item.graphic?.[0].videoWidth / item.graphic?.[0].videoHeight;
                } else {
                    aspectRatio = item.graphic?.[0].image[0].aspectRatio;
                }
                const screenHeight = window.innerHeight;
                let assumedHeight = screenHeight / 1.5;
                if (aspectRatio) {
                    const viewportWidth = this.window.innerWidth;
                    const gap = viewportWidth <= 850 ? 20 : 40;
                    let assumedWidth = (this.containerWidth - gap) / 2;
                    assumedHeight = assumedWidth / aspectRatio;
                }
                heights[previousDouble.index] = assumedHeight;
            }

            this.heights = heights;
        },
        calculateCombinedHeight(item1, item2) {
            let gap = 40;
            const viewportWidth = this.window.innerWidth;
            if (viewportWidth <= 850) {
                gap = 20;
            }
            let isImage1 = !!(item1.graphic?.[0].image?.[0]);
            let isImage2 = !!(item2.graphic?.[0].image?.[0]);
            let isManualAspect1 = !!(item1 && item1.graphic?.[0].videoWidth && item1.graphic?.[0].videoHeight);
            let isManualAspect2 = !!(item2 && item2.graphic?.[0].videoWidth && item2.graphic?.[0].videoHeight);
            if ((isImage1 || isManualAspect1) && (isImage2 || isManualAspect2)) {
                let aspectRatio1, aspectRatio2;
                if (item1 && item1.graphic?.[0].videoWidth && item1.graphic?.[0].videoHeight) {
                    aspectRatio1 = item1.graphic?.[0].videoWidth / item1.graphic?.[0].videoHeight;
                } else {
                    aspectRatio1 = item1.graphic?.[0].image[0].aspectRatio;
                }
                if (item2 && item2.graphic?.[0].videoWidth && item2.graphic?.[0].videoHeight) {
                    aspectRatio2 = item2.graphic?.[0].videoWidth / item2.graphic?.[0].videoHeight;
                } else {
                    aspectRatio2 = item2.graphic?.[0].image[0].aspectRatio;
                }
                const combinedAspectRatio = aspectRatio1 + aspectRatio2;
                const totalWidth = this.containerWidth - gap;
                const height = totalWidth / combinedAspectRatio;
                return Math.floor(height);
            }
            return 0;
        },
        reorderJsonForOptimalPairings() {
            const result = [];
            let items = [...this.filteredJson];
            while (items.length > 0) {
                const item = items.shift();
                if (item.itemType.value === 'doubleColumn') {
                    result.push(item);
                    for (let i = 0; i < items.length; i++) {
                        if (items[i].itemType.value === 'doubleColumn') {
                            result.push(items[i]);
                            items.splice(i, 1);
                            break;
                        }
                    }
                } else {
                    result.push(item);
                }
            }
            this.processedJson = result;
        },
        getCategoryTags(item) {
            let ret = '';
            if (item.projectCategory && Array.isArray(item.projectCategory)) {
                item.projectCategory.forEach(category => {
                    ret += 'cat-' + category.slug + ' ';
                });
            } else if (item.linkout?.cats && Array.isArray(item.linkout.cats)) {
                item.linkout.cats.forEach(category => {
                    ret += 'cat-' + category.slug + ' ';
                });
            }
            return ret.trim();
        },
        getGridLabel(label) {
            let ret = gh.removeTags(label);
            ret = ret.replace(/ /g, '&nbsp;');
            if (this.section === 'home') {
                if (this.outerIndex === 0) {
                    ret = '<span class="dot"></span><b>Featured</b>&nbsp;Work';
                } else if (this.outerIndex === 1) {
                    ret = gh.removeTags(label);
                    const parts = ret.split(' ');
                    ret = parts.map((part, index) => `<div class="scaling-text-l${index + 1}">${part}</div>`).join('');
                }
            }
            return ret;
        },
        clickFilter(filter) {
            if (filter.title === 'Motion') {
                filter = [
                    { title: 'OOH', slug: 'ooh' },
                    { title: 'Digital', slug: 'digital' },
                    { title: 'Social', slug: 'social' }
                ];
            } else if (filter.title === 'Strategy') {
                filter = [
                    { title: 'Social', slug: 'social' }
                ];
            } else {
                filter.slug = filter.title.toLowerCase();
                filter.slug = filter.slug.replace(' ', '-');
                filter = [filter];
            }
            this.$store.commit('setDeepFilterLink', filter);
            this.$router.push({ path: '/work' });
        },
        loadMore() {
            this.visibleCount += this.paginate;
            // The button will auto-hide once visibleCount >= processedJson.length
        },
    }
};
</script>
